/**
 * API Endpoints Configuration
 * Centralized endpoint definitions for the MyWorkAI application
 * Note: Base URL already includes /api/v1, so endpoints are relative to that
 */

export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh-token',
    ME: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    CHANGE_PASSWORD: '/auth/change-password',
  },

  // User endpoints
  USERS: {
    BASE: '/users',
    BY_ID: (id) => `/users/${id}`,
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
  },

  // Homework endpoints
  HOMEWORK: {
    BASE: '/homework',
    BY_ID: (id) => `/homework/${id}`,
    CREATE: '/homework',
    UPDATE: (id) => `/homework/${id}`,
    DELETE: (id) => `/homework/${id}`,
    PUBLISH: (id) => `/homework/${id}/publish`,
    STUDENT_LIST: '/homework/student/list',
    STUDENT_CALENDAR: '/homework/student/calendar',
    START: (id) => `/homework/${id}/start`,
  },

  // Submission endpoints
  SUBMISSIONS: {
    BASE: '/submissions',
    BY_ID: (id) => `/submissions/${id}`,
    SUBMIT_ANSWER: (id) => `/submissions/${id}/answer`,
    SUBMIT: (id) => `/submissions/${id}/submit`,
    RESULTS: (id) => `/submissions/${id}/results`,
  },

  // Class/Course endpoints (from MyClass)
  CLASSES: {
    BASE: '/classes',
    BY_ID: (id) => `/classes/${id}`,
    STUDENTS: (id) => `/classes/${id}/students`,
  },

  // MyExam online exams (CORS backend → Supabase myexam_*)
  EXAMS: {
    BASE: '/exams',
    BY_ID: (id) => `/exams/${id}`,
    CREATE: '/exams',
    UPDATE: (id) => `/exams/${id}`,
    DELETE: (id) => `/exams/${id}`,
    PUBLISH: (id) => `/exams/${id}/publish`,
    QUESTIONS: (id) => `/exams/${id}/questions`,
    IMPORT_QUESTIONS: (id) => `/exams/${id}/questions/import`,
    AVAILABLE: '/exams/available',
    STUDENT_AVAILABLE: '/exams/student/available',
  },

  // My StudyCard practice packs (CORS → mystudycard_*)
  STUDYCARDS: {
    BASE: '/studycards',
    AVAILABLE: '/studycards/available',
    DEFAULTS: '/studycards/defaults',
    ANALYSIS: '/studycards/analysis',
    BY_ID: (id) => `/studycards/${id}`,
    PUBLISH: (id) => `/studycards/${id}/publish`,
    UNPUBLISH: (id) => `/studycards/${id}/unpublish`,
    SYSTEM_DEFAULT: (id) => `/studycards/${id}/system-default`,
    ASSIGN: (id) => `/studycards/${id}/assign`,
    UNASSIGN: (id) => `/studycards/${id}/unassign`,
    IMPORT_QUESTIONS: (id) => `/studycards/${id}/questions/import`,
    REMOVE_QUESTION: (id, questionId) => `/studycards/${id}/questions/${questionId}`,
    REORDER: (id) => `/studycards/${id}/items/reorder`,
  },


  QUESTIONS: {
    BASE: '/questions',
    BY_ID: (id) => `/questions/${id}`,
    CREATE: '/questions',
    UPDATE: (id) => `/questions/${id}`,
    DELETE: (id) => `/questions/${id}`,
    IMPORT: '/questions/import',
  },

  QUESTION_SOURCES: {
    LIST: '/question-sources',
    QUESTIONS: (sourceId) => `/question-sources/${sourceId}/questions`,
    FILTERS: (sourceId) => `/question-sources/${sourceId}/filters`,
  },

  ATTEMPTS: {
    BASE: '/attempts',
    BY_ID: (id) => `/attempts/${id}`,
    START: (examId) => `/exams/${examId}/start`,
    SUBMIT_ANSWER: (attemptId) => `/attempts/${attemptId}/answer`,
    COMPLETE: (attemptId) => `/attempts/${attemptId}/complete`,
    RESULTS: (attemptId) => `/attempts/${attemptId}/results`,
    MY_ATTEMPTS: '/attempts/my',
  },

  ANALYTICS: {
    EXAM_PERFORMANCE: (examId) => `/exams/${examId}/analytics`,
  },

  TEACHER: {
    STUDENT_PERFORMANCE: '/teacher/students/performance',
  },

  GRADING: {
    PENDING: '/grading/pending',
    GRADE: (attemptId, questionId) => `/grading/${attemptId}/questions/${questionId}`,
  },

  // DSERight — DB-backed ExamRight clones (CORS Worker → Supabase)
  DSERIGHT: {
    P1_LIST: '/dseright/p1',
    P1_ANALYSIS: '/dseright/p1/analysis',
    P1_SKILLBOOKS: '/dseright/p1/skillbooks',
    P1_SKILLBOOK: (slug) => `/dseright/p1/skillbooks/${encodeURIComponent(slug)}`,
    P1_BY_SLUG: (slug) => `/dseright/p1/${encodeURIComponent(slug)}`,
    P1_SVG_DESIGN: (slug) =>
      `/dseright/p1/${encodeURIComponent(slug)}/svg-design-sequence`,
    P2_MC_LIST: '/dseright/p2-mc',
    P2_MC_ANALYSIS: '/dseright/p2-mc/analysis',
    P2_MC_SKILLBOOKS: '/dseright/p2-mc/skillbooks',
    P2_MC_SKILLBOOK: (slug) => `/dseright/p2-mc/skillbooks/${encodeURIComponent(slug)}`,
    P2_MC_BY_SLUG: (slug) => `/dseright/p2-mc/${encodeURIComponent(slug)}`,
    P2_MC_SVG_DESIGN: (slug) =>
      `/dseright/p2-mc/${encodeURIComponent(slug)}/svg-design-sequence`,
  },

  // Health check
  HEALTH: '/health',
};

export default API_ENDPOINTS;

